import { useEffect, useState } from 'react';
import { fetchAdminUsersCount, fetchWaitingListStats, fetchDashboardStats } from '../services/adminApi';

const badgeEmojis = {
  'clipper': '✂️',
  'creator': '🎮',
  'curator': '🎬',
  'collector': '📚',
};

const resolveIconUrl = (iconUrl) => {
  /* c8 ignore start */
  if (!iconUrl) return null;
  if (/^https?:\/\//i.test(iconUrl)) return iconUrl;
  return iconUrl.startsWith('/') ? iconUrl : `/${iconUrl}`;
  /* c8 ignore stop */
};

function AdminDashboard() {
  const [totalUsers, setTotalUsers] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [wlStats, setWlStats] = useState(null);
  const [loadingWl, setLoadingWl] = useState(true);
  const [dashStats, setDashStats] = useState(null);
  const [loadingDashStats, setLoadingDashStats] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalUsers, setModalUsers] = useState(null);
  const [modalNote, setModalNote] = useState(null);

  /* c8 ignore start */
  async function handleCardClick(type, payload) {
    if (type === 'badge') {
      setModalTitle(payload.name || 'Badge users');
      setModalOpen(true);
      setModalLoading(true);
      setModalUsers(null);
      setModalNote(null);
      try {
        const badgeId = payload.id;
        const res = await fetch(`/api/admin/reports/badge-users?badge_id=${badgeId}`, { credentials: 'same-origin' });
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            setModalNote('Admin access required to view awarded users.');
            setModalUsers(null);
          } else {
            setModalNote('Unable to load awarded users.');
            setModalUsers(null);
          }
        } else {
          const data = await res.json();
          const badge = (data.badges && data.badges[0]) || { users: [] };
          const users = badge.users || [];
          if (users.length === 0 && (payload.unlocked_count || 0) > 0) {
            setModalNote(`${payload.unlocked_count} user(s) have earned this badge. Only Clipto users are listed here.`);
            setModalUsers([]);
          } else {
            setModalUsers(users);
            setModalNote(null);
          }
        }
      } catch (e) {
        setModalNote('Error fetching awarded users.');
        setModalUsers(null);
      } finally {
        setModalLoading(false);
      }
    } else {
      setModalTitle(payload.title || 'Details');
      setModalUsers(null);
      setModalOpen(true);
    }
  }
  /* c8 ignore stop */

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoadingUsers(true);
        const res = await fetchAdminUsersCount();
        /* c8 ignore start */
        if (mounted) setTotalUsers(res.total);
        /* c8 ignore stop */
      } catch (err) {
        /* c8 ignore start */
        if (mounted) setTotalUsers(null);
        /* c8 ignore stop */
      } finally {
        /* c8 ignore start */
        if (mounted) setLoadingUsers(false);
        /* c8 ignore stop */
      }
    }
    load();
    // load waiting list stats
    /* c8 ignore start */
    (async () => {
      let mountedW = true;
      try {
        setLoadingWl(true);
        const s = await fetchWaitingListStats();
        if (mountedW) setWlStats(s);
      } catch (e) {
        if (mountedW) setWlStats(null);
      } finally {
        if (mountedW) setLoadingWl(false);
      }
      return () => { mountedW = false; };
    })();
    /* c8 ignore stop */

    // Load dashboard statistics
    /* c8 ignore start */
    (async () => {
      let mountedD = true;
      try {
        setLoadingDashStats(true);
        const stats = await fetchDashboardStats();
        if (mountedD) setDashStats(stats);
      } catch (e) {
        console.error('Error loading dashboard stats:', e);
        // Fallback to public stats for non-admin viewers so cards show some data
        try {
          const res = await fetch('/api/public/stats');
          if (res.ok) {
            const pub = await res.json();
            if (mountedD) setDashStats({
              total_clips: pub.total_clips_curated,
              total_public_profiles: pub.total_channels,
              total_users: pub.total_users,
              badge_counts: []
            });
          } else {
            if (mountedD) setDashStats(null);
          }
        } catch (err2) {
          if (mountedD) setDashStats(null);
        }
      } finally {
        if (mountedD) setLoadingDashStats(false);
      }
      return () => { mountedD = false; };
    })();
    /* c8 ignore stop */

    return () => { mounted = false; };
  }, []);

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Stats cards */}
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm font-medium">Total Users</h3>
            <span role="img" aria-label="Users" className="text-2xl">👥</span>
          </div>
          <p className="text-3xl font-bold text-white">{loadingUsers ? '…' : (totalUsers !== null ? totalUsers : '-')}</p>
          <p className="text-xs text-gray-500 mt-2">{loadingUsers ? 'Loading…' : (totalUsers !== null ? 'Total registered users' : 'Unavailable')}</p>
        </div>

        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm font-medium">Waiting List</h3>
            <span role="img" aria-label="Waiting list" className="text-2xl">📋</span>
          </div>
          <p className="text-3xl font-bold text-white">{loadingWl ? '…' : (wlStats && (wlStats.total != null ? (wlStats.total === 0 ? 'Empty' : wlStats.total) : 'Empty'))}</p>
          <p className="text-xs text-gray-500 mt-2">{loadingWl ? 'Loading…' : (wlStats ? `Capacity: ${wlStats.capacity ?? '–'} ${wlStats.is_full ? '(full)' : ''}` : 'Unavailable')}</p>
        </div>

        {/* Total Clips Card */}
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm font-medium">Total Clips</h3>
            <span role="img" aria-label="Clips" className="text-2xl">🎬</span>
          </div>
          <p className="text-3xl font-bold text-white">{loadingDashStats ? '…' : (dashStats?.total_clips ?? '-')}</p>
          <p className="text-xs text-gray-500 mt-2">Clips curated on platform</p>
        </div>

        {/* Total Tags Card */}
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm font-medium">Total Tags</h3>
            <span role="img" aria-label="Tags" className="text-2xl">🔖</span>
          </div>
          <p className="text-3xl font-bold text-white">{loadingDashStats ? '…' : (dashStats?.total_tags ?? '-')}</p>
          <p className="text-xs text-gray-500 mt-2">Unique tags used across clips</p>
        </div>

        {/* Active Creators (Clipto users whose clips are on Clipto) */}
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm font-medium">Active Creators</h3>
            <span role="img" aria-label="Creators" className="text-2xl">🎮</span>
          </div>
          <p className="text-3xl font-bold text-white">{loadingDashStats ? '…' : (dashStats?.active_creators ?? '-')}</p>
          <p className="text-xs text-gray-500 mt-2">Clipto users whose clips from their channel are on Clipto</p>
        </div>

        {/* Active Collectors */}
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm font-medium">Active Collectors</h3>
            <span role="img" aria-label="Collectors" className="text-2xl">📚</span>
          </div>
          <p className="text-3xl font-bold text-white">{loadingDashStats ? '…' : (dashStats?.active_collectors ?? '-')}</p>
          <p className="text-xs text-gray-500 mt-2">Clipto users collecting others' clips</p>
        </div>

        {/* Active Clippers (users who added their own clips) */}
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm font-medium">Active Clippers</h3>
            <span role="img" aria-label="Clippers" className="text-2xl">✂️</span>
          </div>
          <p className="text-3xl font-bold text-white">{loadingDashStats ? '…' : (dashStats?.active_clippers ?? '-')}</p>
          <p className="text-xs text-gray-500 mt-2">Clipto users who made clips that were added to Clipto</p>
        </div>

        {/* Public Profiles */}
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm font-medium">Public Profiles</h3>
            <span role="img" aria-label="Public profiles" className="text-2xl">🌍</span>
          </div>
          <p className="text-3xl font-bold text-white">{loadingDashStats ? '…' : (dashStats?.total_public_profiles ?? '-')}</p>
          <p className="text-xs text-gray-500 mt-2">Clipto users with public profiles enabled</p>
        </div>
      </div>

      {/* Badges rendered as uniform cards with images (match stat cards) */}
      <div className="mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {loadingDashStats ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-dark-800 rounded-lg p-6 border border-dark-700 h-36" />
            ))
          ) : (
            (dashStats?.badge_counts || []).map((b) => (
              <div key={b.id} role="button" onClick={() => handleCardClick('badge', b)} className="text-left bg-dark-800 rounded-lg p-6 border border-dark-700 cursor-pointer hover:border-dark-600">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-gray-400 text-sm font-medium">{b.name}</h3>
                  {b.icon_url ? (
                    <img
                      src={resolveIconUrl(b.icon_url)}
                      alt={b.name}
                      className="w-8 h-8 rounded"
                      onError={(e) => { e.target.onerror = null; e.target.src = '/assets/badges/default.png'; }}
                    />
                  ) : (
                    <span role="img" aria-label={b.name} className="text-2xl">{badgeEmojis[b.key?.toLowerCase()] || '🏅'}</span>
                  )}
                </div>
                <p className="text-3xl font-bold text-white">{b.unlocked_count}</p>
                <p className="text-xs text-gray-500 mt-2">Users unlocked this badge</p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal for card details */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)} />
          <div className="relative w-full max-w-2xl mx-4 bg-dark-900 border border-dark-700 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{modalTitle}</h3>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-white">✕</button>
            </div>
            <div className="max-h-80 overflow-auto">
              {modalLoading ? (
                <p>Loading…</p>
              ) : modalNote ? (
                <div>
                  <p className="text-sm text-gray-400 mb-3">{modalNote}</p>
                </div>
              ) : modalUsers !== null ? (
                /* c8 ignore start */
                modalUsers.length === 0 ? (
                  <p className="text-sm text-gray-400">No Clipto users have earned this badge.</p>
                ) : (
                  <ul className="space-y-2">
                    {modalUsers.map(u => (
                      <li key={u.user_id} className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-white">{u.display_name || u.username}</div>
                          <div className="text-xs text-gray-500">{u.username}</div>
                        </div>
                        <div className="text-xs text-gray-400">{new Date(u.earned_at).toLocaleString()}</div>
                      </li>
                    ))}
                  </ul>
                )
                /* c8 ignore stop */
              ) : (
                /* c8 ignore next */
                <p className="text-sm text-gray-400">No details available. Open Reports for more.</p>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminDashboard;
